import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Activity, Clock, Search, Layers, User, RefreshCw, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import { API_BASE } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

/**
 * Pantalla temporal de diagnóstico del Habita Advisor.
 * Solo admin. Permite auditar cómo el Advisor está construyendo Contexts
 * mientras el usuario utiliza la plataforma.
 */

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-800',
  idle: 'bg-amber-100 text-amber-800',
  completed: 'bg-blue-100 text-blue-800',
  archived: 'bg-gray-200 text-gray-700',
  deleted: 'bg-rose-100 text-rose-800',
  purged: 'bg-black/80 text-white',
};

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700';
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded ${c}`}>{status}</span>;
}

function useAdvisorFetch(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('habita_token');
    fetch(`${API_BASE}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!cancelled) setData(j); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path, tick]);
  const reload = () => setTick(t => t + 1);
  return { data, loading, error, reload };
}

function Stats() {
  const { data, loading, reload } = useAdvisorFetch('/advisor/admin/stats');
  if (loading || !data) return <div className="h-24 rounded-xl bg-gray-100 animate-pulse" />;
  const kpis = [
    { label: 'Contexts totales', value: data.total_contexts, icon: Layers },
    { label: 'Recomendaciones', value: data.recommendations_total ?? 0, icon: Sparkles },
    { label: 'Últimas 24h', value: data.contexts_last_24h, icon: Clock },
    { label: 'Eventos totales', value: data.decisions_total, icon: Search },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {kpis.map((k) => (
        <div key={k.label} className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-widest">
            <k.icon className="w-3.5 h-3.5" /> {k.label}
          </div>
          <p className="mt-2 font-serif text-2xl font-bold text-habita-dark">{k.value}</p>
        </div>
      ))}
      <div className="col-span-2 md:col-span-4 bg-white border border-gray-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-habita-dark">Distribución por estado</h3>
          <button onClick={reload} className="text-xs text-habita hover:underline inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refrescar
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.by_status).map(([s, n]) => (
            <div key={s} className="inline-flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full text-xs">
              <StatusPill status={s} /> <strong>{n}</strong>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <h4 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Por punto de entrada</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data.by_entry_point || {}).map(([e, n]) => (
              <span key={e} className="inline-flex items-center gap-2 bg-habita-mint text-habita-dark px-3 py-1.5 rounded-full text-xs font-medium">
                {e} · <strong>{n}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ContextRow({ ctx, onSelect, isSelected }) {
  const dt = new Date(ctx.updated_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
  return (
    <button
      onClick={() => onSelect(ctx.id)}
      data-testid={`ctx-row-${ctx.id}`}
      className={`w-full text-left px-3 py-3 border-b border-gray-100 hover:bg-habita-mint/40 transition-colors ${isSelected ? 'bg-habita-mint/60' : ''}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[11px] text-gray-500 truncate">{ctx.id}</p>
          <p className="text-sm font-medium text-habita-dark truncate">{ctx.intent || '—'}</p>
          <p className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
            <StatusPill status={ctx.status} />
            <span className="uppercase tracking-widest">{ctx.entry_point}</span>
            <span>· rev {ctx.revision_number}</span>
            <span>· {ctx.events_count ?? 0} events</span>
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[11px] text-gray-400">{dt}</p>
        </div>
      </div>
    </button>
  );
}

function RecommendationsPanel({ contextId }) {
  const { data, loading } = useAdvisorFetch(`/advisor/admin/contexts/${contextId}/recommendations`);
  if (loading) return <div className="h-40 rounded-xl bg-gray-100 animate-pulse" />;
  const items = data?.items || [];
  if (!items.length) return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center text-sm text-gray-500">
      <Sparkles className="w-6 h-6 mx-auto text-gray-300 mb-2" />
      Este Context aún no ha generado recomendaciones.
    </div>
  );
  return (
    <div className="space-y-4">
      {items.map((rec) => (
        <div key={rec.id} className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={`rec-${rec.id}`}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-gray-500">Estrategia · {rec.strategy}</p>
              <p className="font-mono text-xs text-gray-500 break-all">{rec.id}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-widest text-gray-500">Tiempo</p>
              <p className="text-sm font-semibold text-habita-dark">{rec.elapsed_ms} ms</p>
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 text-xs mb-3">
            <div><p className="text-gray-500">Candidatos</p><p className="font-semibold">{rec.candidates_evaluated}</p></div>
            <div><p className="text-gray-500">Descartados</p><p className="font-semibold">{rec.candidates_discarded}</p></div>
            <div><p className="text-gray-500">Recomendados</p><p className="font-semibold">{rec.items?.length || 0}</p></div>
            <div><p className="text-gray-500">Score promedio</p><p className="font-semibold">{(rec.avg_score * 100).toFixed(0)}%</p></div>
            <div><p className="text-gray-500">Prefs usadas</p><p className="font-semibold">{rec.preferences_used?.length || 0}</p></div>
          </div>
          <div className="space-y-3">
            {rec.items?.map((it) => (
              <details key={it.item_id} className="rounded-lg border border-gray-100 bg-gray-50">
                <summary className="cursor-pointer px-3 py-2 flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span className="inline-block w-6 h-6 text-[11px] rounded-full bg-habita text-white grid place-items-center font-semibold">#{it.rank}</span>
                    <span className="font-medium text-habita-dark truncate">{it.snapshot?.title || it.item_id.slice(0, 8)}</span>
                  </span>
                  <span className="text-xs font-semibold text-habita">{(it.score * 100).toFixed(0)}% · conf {(it.confidence?.score * 100).toFixed(0)}%</span>
                </summary>
                <div className="px-3 pb-3 space-y-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Explicación</p>
                    <p className="text-xs text-gray-800">{it.explanation?.human_readable}</p>
                    <ul className="mt-1 text-[11px] text-gray-600 list-disc pl-4">
                      {(it.explanation?.reasoning_steps || []).map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                  {(it.explanation?.evidence_refs?.length > 0) && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Evidencia usada</p>
                      <div className="flex flex-wrap gap-1">
                        {it.explanation.evidence_refs.map((e, i) => (
                          <span key={i} className="text-[10px] font-mono bg-white border border-gray-200 rounded px-1.5 py-0.5">{e}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1">Positivos</p>
                      <ul className="text-[11px] text-gray-700 space-y-0.5">
                        {(it.breakdown?.positive_factors || []).map((f, i) => (
                          <li key={i}>• {f.key} <span className="text-gray-400">({(f.contribution || 0).toFixed(3)})</span></li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-rose-700 mb-1">Negativos</p>
                      <ul className="text-[11px] text-gray-700 space-y-0.5">
                        {(it.breakdown?.negative_factors || []).map((f, i) => (
                          <li key={i}>• {f.key} <span className="text-gray-400">({(f.contribution || 0).toFixed(3)})</span></li>
                        ))}
                        {!(it.breakdown?.negative_factors || []).length && <li className="text-gray-400 italic">Ninguno</li>}
                      </ul>
                    </div>
                  </div>
                </div>
              </details>
            ))}
          </div>
          {rec.discarded?.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-gray-500 hover:text-habita">
                Ver descartados ({rec.discarded.length})
              </summary>
              <ul className="mt-2 text-[11px] text-gray-600 space-y-0.5 max-h-40 overflow-y-auto">
                {rec.discarded.slice(0, 20).map((d, i) => (
                  <li key={i}>· <span className="uppercase text-[9px] tracking-widest text-gray-400">{d.stage}</span> {d.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}

function TimelineView({ contextId }) {
  const [tab, setTab] = React.useState('timeline');
  const { data, loading, reload } = useAdvisorFetch(`/advisor/admin/contexts/${contextId}/timeline`);
  if (loading) return <div className="h-64 rounded-xl bg-gray-100 animate-pulse" />;
  if (!data) return <p className="text-sm text-gray-500">No hay datos.</p>;
  const { context, decisions, analytics } = data;

  const combined = [
    ...decisions.map(d => ({ kind: 'decision', at: d.occurred_at, type: d.type, payload: d.payload, actor: d.actor_id })),
    ...analytics.map(a => ({ kind: 'analytics', at: a.created_at, type: a.event_type, payload: a.metadata, actor: a.user_id })),
  ].sort((x, y) => new Date(x.at) - new Date(y.at));

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-gray-500">Context</p>
            <p className="font-mono text-xs text-gray-700 break-all">{context.id}</p>
          </div>
          <button onClick={reload} className="text-xs text-habita hover:underline inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refrescar
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
          <div><p className="text-gray-500 uppercase tracking-widest">Estado</p><div className="mt-0.5"><StatusPill status={context.status} /></div></div>
          <div><p className="text-gray-500 uppercase tracking-widest">Entrada</p><p className="font-medium">{context.entry_point}</p></div>
          <div><p className="text-gray-500 uppercase tracking-widest">Intento</p><p className="font-medium truncate">{context.intent || '—'}</p></div>
          <div><p className="text-gray-500 uppercase tracking-widest">Revisiones</p><p className="font-medium">{context.revision_number}</p></div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setTab('timeline')}
            data-testid="tab-timeline"
            className={`flex-1 px-4 py-3 text-sm font-semibold transition ${tab === 'timeline' ? 'text-habita border-b-2 border-habita bg-habita-mint/30' : 'text-gray-500 hover:text-habita-dark'}`}
          >Timeline · {combined.length}</button>
          <button
            onClick={() => setTab('payload')}
            data-testid="tab-payload"
            className={`flex-1 px-4 py-3 text-sm font-semibold transition ${tab === 'payload' ? 'text-habita border-b-2 border-habita bg-habita-mint/30' : 'text-gray-500 hover:text-habita-dark'}`}
          >Payload</button>
          <button
            onClick={() => setTab('recs')}
            data-testid="tab-recommendations"
            className={`flex-1 px-4 py-3 text-sm font-semibold transition ${tab === 'recs' ? 'text-habita border-b-2 border-habita bg-habita-mint/30' : 'text-gray-500 hover:text-habita-dark'}`}
          >Recomendaciones</button>
        </div>
        <div className="p-4">
          {tab === 'timeline' && (
            <ol className="relative border-l-2 border-gray-200 space-y-3 pl-5">
              {combined.map((ev, i) => {
                const isDecision = ev.kind === 'decision';
                return (
                  <li key={i} className="relative">
                    <span className={`absolute -left-[26px] top-0.5 w-3.5 h-3.5 rounded-full ${isDecision ? 'bg-habita' : 'bg-gray-300'}`} />
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-sm font-medium text-habita-dark">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider mr-2 ${isDecision ? 'bg-habita text-white' : 'bg-gray-200 text-gray-700'}`}>{isDecision ? 'decision' : 'analytics'}</span>
                        {ev.type}
                      </p>
                      <p className="text-[11px] text-gray-400 shrink-0">{new Date(ev.at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'medium' })}</p>
                    </div>
                    {ev.payload && Object.keys(ev.payload).length > 0 && (
                      <pre className="mt-1 text-[10px] font-mono bg-gray-50 rounded p-2 overflow-auto max-h-32">{JSON.stringify(ev.payload, null, 2)}</pre>
                    )}
                  </li>
                );
              })}
              {combined.length === 0 && <li className="text-sm text-gray-500">Aún no hay eventos.</li>}
            </ol>
          )}
          {tab === 'payload' && (
            <div>
              <pre className="text-[11px] font-mono bg-gray-50 rounded-lg p-3 overflow-auto max-h-96">{JSON.stringify(context.payload, null, 2)}</pre>
              {(context.evidence?.length || 0) > 0 && (
                <>
                  <h4 className="text-xs uppercase tracking-widest text-gray-500 mt-4 mb-1">Evidencia</h4>
                  <pre className="text-[11px] font-mono bg-gray-50 rounded-lg p-3 overflow-auto max-h-64">{JSON.stringify(context.evidence, null, 2)}</pre>
                </>
              )}
            </div>
          )}
          {tab === 'recs' && <RecommendationsPanel contextId={contextId} />}
        </div>
      </div>
    </div>
  );
}

export default function AdminAdvisorInspector() {
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [entryFilter, setEntryFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const qs = new URLSearchParams();
  if (statusFilter) qs.set('status', statusFilter);
  if (entryFilter) qs.set('entry_point', entryFilter);
  if (userFilter) qs.set('user_id', userFilter);
  const listPath = `/advisor/admin/contexts?${qs.toString()}`;
  const { data: list, loading: listLoading, reload } = useAdvisorFetch(listPath);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <Link to="/admin" className="text-sm inline-flex items-center gap-1 mb-4 px-3 py-1.5 rounded-full bg-white border border-emerald-600 text-habita-dark font-semibold hover:bg-habita hover:text-white transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" /> Volver al panel
        </Link>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold text-habita-dark">
              Habita Advisor · Inspector
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Herramienta interna de diagnóstico. Audita cómo el Advisor construye Context durante el uso real.
            </p>
          </div>
          <Link
            to="/admin/advisor/metrics"
            data-testid="link-advisor-metrics"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-habita text-white text-sm font-semibold hover:bg-habita-dark transition-colors"
          >
            Panel de métricas →
          </Link>
        </div>

        <div className="mb-6">
          <Stats />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="p-3 border-b border-gray-100 space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-habita-dark">
                  Contexts recientes {list ? `(${list.total})` : ''}
                </h2>
                <button onClick={reload} className="text-xs text-habita hover:underline inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Refrescar
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-9 text-xs rounded-lg border border-gray-200 px-2"
                  data-testid="filter-status"
                >
                  <option value="">Todos</option>
                  <option value="active">Active</option>
                  <option value="idle">Idle</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                  <option value="deleted">Deleted</option>
                </select>
                <select
                  value={entryFilter}
                  onChange={(e) => setEntryFilter(e.target.value)}
                  className="h-9 text-xs rounded-lg border border-gray-200 px-2"
                  data-testid="filter-entry"
                >
                  <option value="">Origen…</option>
                  <option value="search">search</option>
                  <option value="property_detail">property_detail</option>
                  <option value="crm">crm</option>
                  <option value="home">home</option>
                  <option value="other">other</option>
                </select>
                <Input
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  placeholder="user_id"
                  className="h-9 text-xs"
                  data-testid="filter-user"
                />
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {listLoading && <div className="p-4 text-sm text-gray-500">Cargando…</div>}
              {list && list.items.length === 0 && (
                <div className="p-6 text-sm text-gray-500 text-center">
                  <User className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                  No hay contextos con esos filtros.
                </div>
              )}
              {list?.items.map(c => (
                <ContextRow
                  key={c.id}
                  ctx={c}
                  onSelect={setSelected}
                  isSelected={selected === c.id}
                />
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            {selected ? (
              <TimelineView contextId={selected} />
            ) : (
              <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-gray-500">
                <Layers className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <p className="text-sm">Selecciona un Context de la lista para inspeccionar su timeline.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
